import axios, { AxiosInstance, AxiosRequestConfig, CancelToken } from 'axios';
import {
  Settings,
  IBankSearchRequest,
  IBankSearchResponse,
  IBankItem,
  ITokenParameters,
  IAuthoriseResponse,
  IBankStrategy,
  IBankStrategyRequest,
  ISignupState,
  OpenBankingProcessState,
  IOpenbankingStrategyRequest,
  IErrorRequest,
  CompanySearchItem,
  IProcessBankStrategyYodlee,
  IProcessBankResponse,
  IBankProvider,
  ISignupAppConfig,
  IAuditRequest,
  IProgressResponse,
  IPdfFileTransfer,
  IAddressSearchRequest,
  IAddressSearchResponse,
  IConnectors,
  IValidisRequestWithExternalReference,
  IValidisConfigDetailsResponse,
  IValidisUploadRequest,
  IValidisUploadResponse,
  IReconsent,
  IFinacialCircumstances,
  IIncome,
  IHouseholdComposition,
  IMonthlyExpenditure,
  IReverseLookupInfoRequest,
  IReverseLookupResponses
} from '@/api/signup/interface';
import { clientResolution } from '@/util/client';
import { logger } from '@/util/logger';
import { SignupProgressStateEnum } from '@/types/enum';

const api = location.host.startsWith('localhost:')
  ? 'https://localhost:44389'
  : location.host.includes('-opdt')
  ?location.protocol + '//' + ['signup-opdt', ...location.host.split('.').splice(1)].join('.') :location.protocol + '//' + ['signup', ...location.host.split('.').splice(1)].join('.');

// const api = 'https://signup.consents-dev.online';

// const api = 'https://signup.consents.online';

export const apiURL = api;

class SignupClient {
  private readonly client: AxiosInstance;

  constructor() {
    const baseURL = process.env.API || api || 'https://localhost:44389';
    this.client = axios.create({
      // baseURL: 'https://signupapi.consents-dev.online/',
      baseURL, // SSL
      // baseURL: 'http://localhost:63961', // NON-SSL
    });

    logger.debug('using api: %s', baseURL);

    this.client.interceptors.request.use((config) => {
      config.headers.client = clientResolution.clientCode;
      return config;
    });
  }

  public authorise = (data: ITokenParameters) =>
    this.post<IAuthoriseResponse>({
        url: '/api/token/authorise' + '?t=' + new Date().valueOf() /* IE workaround for no-cache ignore */,
      data,
    });

  public getSettings = () =>
    this.get<Settings>({
        url: '/api/settings' + '?t=' + new Date().valueOf() /* IE workaround for no-cache ignore */,
    });

  public getAppConfig = () =>
    this.get<ISignupAppConfig>({
        url: '/api/settings/config' + '?t=' + new Date().valueOf() /* IE workaround for no-cache ignore */,
      });
    
  public triggerWebHookEvent = (configName: string) =>
    this.post<Boolean>({
      url: '/api/customer/triggerWebHookEvent/' + configName
  });

  public getPopularBanks = (size: number, allowedProviders?: number[]) =>
      this.post<IBankItem[]>({
          url: '/api/bank/popular/',
      data: { size, allowedProviders },
    });

  public searchBank = (data: IBankSearchRequest) =>
    this.post<IBankSearchResponse>({
      url: '/api/bank/search',
      data,
    });

  public bankProvider = (bankId: string, allowedProviders?: number[]) =>
    this.post<IBankProvider>({
      url: '/api/bank/provider',
      data: { bankId, allowedProviders },
    });

  public bankStrategy = (data: IBankStrategyRequest) =>
    this.post<IBankStrategy>({
      url: '/api/bank/strategy',
      data,
    });

  public processBankStrategy = (data: IProcessBankStrategyYodlee) =>
    this.post<IProcessBankResponse>({
      url: '/api/bank/process',
      data,
    });

  public reverseLookUpInfoRequest = (data:IReverseLookupInfoRequest) =>
    this.post<IReverseLookupResponses>({
      url: '/api/customer/lookupinfo',
      data,
      timeout:data.timeOut,
    });

  public fetchAccountPdf = (state: string) =>
    this.post<IPdfFileTransfer>({
      url: '/api/bank/account/pdf',
      data: { state },
    });

  public openbankingStrategy = (data: IOpenbankingStrategyRequest) =>
    this.post<IBankStrategy>({
      url: '/api/bank/strategy/openbanking',
      data,
    });

  public mastercardStrategy = (data: any) =>
    this.get<any>({
      url: '/api/mastercard/processredirect',
      params: data,
    });

  public customerState = (page: string, data: ISignupState) =>
    this.post<boolean>({
      url: '/api/customer/state/' + page,
      data,
    });
  
    public saveApplyplus = (requestId: string, data: ISignupState) =>
    this.post<boolean>({
      url: '/api/customer/save/applyplus/'+ requestId,
      data,
    });
      
    public customerProgress = (signupProgressState: SignupProgressStateEnum) =>
      this.post<boolean>({
        url: '/api/customer/progress',
        data: { signupProgressState},
      });

    public checkCustomerFirstSignupComplete = () =>
      this.get<boolean>({
        url: '/api/customer/checkHasCompleteFirstSignup/',
      });

    public createEngagement = (companyName: string) =>
      this.post<boolean>({
          url: '/api/customer/createEngagement',
          data: { companyName},
    });

  public companyInfo = (companyNumber: string) =>
    this.get<CompanySearchItem>({
      url: '/api/lookup/company/' + companyNumber,
    });

  public completeUrl = () =>
    this.get<string>({
      url: '/api/token/complete',
    });
    
  public cancelUrl = () =>
  this.get<string>({
    url: '/api/token/cancel',
  });

  public earlyRedirectUrl = () =>
  this.get<string>({
    url: '/api/token/earlyredirect',
  });
  
  public getCustomerScenarioMessage = () =>
    this.get<string>({
        url: '/api/customer/customerScenarioMessage',
    });

  public requestProgress = (requestId: string) =>
    this.get<IProgressResponse>({
        url: '/api/bank/progress/' + encodeURIComponent(requestId) + '?t=' + new Date().valueOf() /* IE workaround for no-cache ignore */
    });
    
  public error = (data: IErrorRequest) =>
    this.post<any>({
      url: '/api/log/error',
      data,
    });

  public audit = (data: IAuditRequest) =>
    this.post<any>({
      url: '/api/log/audit',
      data,
    }).catch((x) => null);

  public validatePage = (page: string, data: object) =>
    this.post<boolean>({
      url: '/api/customer/page/validate/' + page,
      data,
    });

  public searchAddress = (data: IAddressSearchRequest) =>
    this.post<IAddressSearchResponse>({
      url: '/api/lookup/address/search',
      data,
      });

    public connectors = (externalReference: string) =>
    this.get<IConnectors[]>({
        url: '/api/validisdata/connectors/' + externalReference,
    });

  public getEngagementUploadStatus = (engagementId: number) =>
    this.get<string>({
          url: '/api/validisdata/getUploadStatus/' + engagementId,
    });

  public validisPostUpload = (data: IValidisUploadRequest) =>
      this.post<IValidisUploadResponse>({
        url: '/api/validisdata/postUpload',
        data,
      });

  public validisConfigDetails = (data: ISignupState) =>
    this.post<IValidisConfigDetailsResponse>({
        url: '/api/customer/getCustomerValidisDetails',
      data,
   });
   
  public isUsernameAvailable = (username: string) =>
    this.post<Boolean>({
      url: 'api/customer/check/username',
      data: {
        username,
      },
      });

  public register = (username: string, password: string, password2: string) =>
    this.post<Boolean>({
      url: 'api/customer/register',
      data: {
        username,
        password,
        password2,
      },
    }).catch((x) => {
      logger.error(x);
      return false;
    });

  
  public createCollections = (data: any) =>
    this.post<string>({
      url: '/api/customer/createCustomerCollections',
      data,
  });

    public createCollectionsEmployment = (data: any) =>
    this.post<string>({
      url: '/api/customer/createCustomerCollectionsEmployment',
      data,
  });

  public createCollectionsBus = (data: any) =>
    this.post<string>({
      url: '/api/customer/createCustomerCollectionsBus',
      data,
  });

    public createCollectionsBusEmployment = (data: any) =>
    this.post<string>({
      url: '/api/customer/createCustomerCollectionsBusEmployment',
      data,
  });

  public createCollectionsBusRFD = (data: any) =>
  this.post<string>({
    url: '/api/customer/createCustomerCollectionsBusRFD',
    data,
});

  public createCollectionsBau = (data: any) =>
    this.post<string>({
      url: '/api/customer/createCustomerCollectionsBau',
      data,
  });

  public createCollectionsBauPriority = (data: any) =>
    this.post<string>({
      url: '/api/customer/createCustomerCollectionsBauPriority',
      data,
    });

  public getReconsentBanks = (externalRef: string) =>
    this.get<IReconsent[]>({
      url: '/api/customer/signup?externalRef=' + externalRef + '&mode=reconsent',
    });

  public reconsentSites = (sites: any) =>
    this.post<number[]>({
      url: '/api/customer/signup',
      data: {sites}
    });

  public financialCircumstanses = (data: IFinacialCircumstances) =>
    this.post<Boolean>({
      url: '/api/customer/finacialCircumstances',
      data: data
    });

  public income = (data: IIncome) =>
    this.post<Boolean>({
      url: '/api/customer/income',
      data: data
    });

  public householdComposition = (data: IHouseholdComposition) =>
    this.post<Boolean>({
        url: '/api/customer/householdComposition',
        data: data
    });

    public monthlyExpenditure = (data: IMonthlyExpenditure) =>
    this.post<Boolean>({
      url: '/api/customer/monthlyExpenditure',
      data: data
    });

  protected get<T>(config: AxiosRequestConfig) {
    return this.makeRequest<T>({
      ...config,
      method: 'get',
    });
  }
  protected post<T>(config: AxiosRequestConfig) {
    return this.makeRequest<T>({
      ...config,
      method: 'post',
    });
  }

  private makeRequest = <T>(config: AxiosRequestConfig) =>
    this.client.request<T>(config).then((x) => x.data);

  public get interceptors() {
    return this.client.interceptors;
  }
}

export default new SignupClient();